import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, DotsSixVertical, ArrowsClockwise, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ICONS, ICON_NAMES, iconFor } from "@/lib/navIcons";

const ROLE_OPTIONS = [
  "organization_owner", "admin", "sales_manager", "sales_executive",
  "accountant", "dispatch_manager", "marketing_manager", "customer_support", "custom",
];

const EMPTY_MENU = {
  label: "", path: "", icon: "House", parent_id: null,
  enabled: true, roles: [], perm: "", owner_only: false, platform_only: false, badge: "",
};

function IconPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-10 gap-1 max-h-40 overflow-y-auto p-2 border rounded-md bg-secondary/30" data-testid="nav-menu-icon-picker">
      {ICON_NAMES.map((name) => {
        const I = ICONS[name];
        const active = value === name;
        return (
          <button
            key={name}
            type="button"
            onClick={() => onChange(name)}
            title={name}
            className={"w-8 h-8 rounded flex items-center justify-center transition-colors " + (active ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}
          >
            <I size={16} weight="duotone" />
          </button>
        );
      })}
    </div>
  );
}

function RoleChips({ selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="nav-menu-roles">
      {ROLE_OPTIONS.map((r) => {
        const on = selected.includes(r);
        return (
          <button
            key={r}
            type="button"
            onClick={() => onToggle(r)}
            className={"text-[11px] px-2 py-1 rounded-full border transition-colors " + (on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary")}
          >
            {r}
          </button>
        );
      })}
    </div>
  );
}

function MenuDialog({ open, onOpenChange, initial, parents, onSave }) {
  const [f, setF] = useState(EMPTY_MENU);
  useEffect(() => {
    setF(initial ? Object.assign({}, EMPTY_MENU, initial) : EMPTY_MENU);
  }, [initial, open]);
  const toggleRole = (r) => {
    const roles = f.roles.includes(r) ? f.roles.filter((x) => x !== r) : f.roles.concat([r]);
    setF(Object.assign({}, f, { roles }));
  };
  const submit = async () => {
    if (!f.label.trim()) {
      toast.error("Label is required");
      return;
    }
    try {
      await onSave(f);
      onOpenChange(false);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed");
    }
  };
  const Ic = iconFor(f.icon);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="nav-menu-dialog">
        <DialogHeader>
          <DialogTitle>{initial && initial.id ? "Edit menu" : "New menu"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 md:col-span-1">
              <Label className="text-xs">LABEL</Label>
              <Input value={f.label} onChange={(e) => setF(Object.assign({}, f, { label: e.target.value }))} data-testid="nav-menu-label" />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Label className="text-xs">PATH</Label>
              <Input value={f.path} onChange={(e) => setF(Object.assign({}, f, { path: e.target.value }))} placeholder="/custom" data-testid="nav-menu-path" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-2 block">
              ICON — <Ic size={14} className="inline mx-1" weight="duotone" /> {f.icon}
            </Label>
            <IconPicker value={f.icon} onChange={(v) => setF(Object.assign({}, f, { icon: v }))} />
          </div>
          <div>
            <Label className="text-xs">PARENT (leave blank for top-level)</Label>
            <Select
              value={f.parent_id || "__none__"}
              onValueChange={(v) => setF(Object.assign({}, f, { parent_id: v === "__none__" ? null : v }))}
            >
              <SelectTrigger data-testid="nav-menu-parent"><SelectValue placeholder="Top-level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Top-level —</SelectItem>
                {parents.filter((p) => p.id !== (initial && initial.id)).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">PERMISSION (optional, e.g. leads.view)</Label>
              <Input value={f.perm || ""} onChange={(e) => setF(Object.assign({}, f, { perm: e.target.value }))} placeholder="module.action" />
            </div>
            <div>
              <Label className="text-xs">BADGE KEY</Label>
              <Input value={f.badge || ""} onChange={(e) => setF(Object.assign({}, f, { badge: e.target.value }))} placeholder="followupsDue" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">VISIBLE TO ROLES (empty = all)</Label>
            <RoleChips selected={f.roles} onToggle={toggleRole} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={f.enabled} onCheckedChange={(v) => setF(Object.assign({}, f, { enabled: v }))} data-testid="nav-menu-enabled" />
              Enabled
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={f.owner_only} onCheckedChange={(v) => setF(Object.assign({}, f, { owner_only: v }))} />
              Owner only
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={f.platform_only} onCheckedChange={(v) => setF(Object.assign({}, f, { platform_only: v }))} />
              Platform only
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="nav-menu-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MenuRow({ m, depth, kids, isOpen, onToggleExpand, onEnabledChange, onEdit, onDelete, onAddChild, onDragStart, onDragOver, onDrop }) {
  const Ic = iconFor(m.icon);
  const hasKids = kids && kids.length > 0;
  const rowStyle = { paddingLeft: 12 + depth * 20 };
  const rowClass = "flex items-center gap-2 py-2 px-3 rounded-md border border-transparent hover:bg-secondary/40 transition-colors " + (m.enabled ? "" : "opacity-50");
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={rowClass}
      style={rowStyle}
      data-testid={"nav-row-" + m.id}
    >
      <DotsSixVertical size={14} className="text-muted-foreground cursor-grab shrink-0" />
      {hasKids ? (
        <button onClick={onToggleExpand} className="text-muted-foreground shrink-0">
          {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
        </button>
      ) : (
        <span className="w-3 shrink-0" />
      )}
      <div className="w-7 h-7 rounded bg-secondary flex items-center justify-center shrink-0">
        <Ic size={15} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate flex items-center gap-2">
          {m.label}
          {m.is_system && <span className="text-[9px] uppercase tracking-wider text-primary/70 border border-primary/30 px-1 rounded">system</span>}
          {m.owner_only && <span className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1 rounded">owner</span>}
          {m.platform_only && <span className="text-[9px] uppercase tracking-wider text-red-700 dark:text-red-300 border border-red-500/40 px-1 rounded">platform</span>}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {m.path ? m.path : <span className="italic">group header</span>}
          {m.perm ? <span> · perm <span className="font-mono">{m.perm}</span></span> : null}
          {m.roles && m.roles.length ? <span> · roles: {m.roles.join(", ")}</span> : null}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Switch checked={m.enabled} onCheckedChange={onEnabledChange} data-testid={"nav-toggle-" + m.id} />
        <button onClick={onAddChild} className="p-1.5 rounded hover:bg-secondary text-muted-foreground" title="Add submenu" data-testid={"nav-add-child-" + m.id}>
          <Plus size={13} />
        </button>
        <button onClick={onEdit} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={"nav-edit-" + m.id}>
          <PencilSimple size={13} weight="duotone" />
        </button>
        {!m.is_system && (
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={"nav-delete-" + m.id}>
            <Trash size={13} weight="duotone" />
          </button>
        )}
      </div>
    </div>
  );
}

function renderTree(items, byParent, expanded, ctx, depth = 0) {
  const out = [];
  for (const m of items) {
    const kids = byParent[m.id] || [];
    const isOpen = expanded[m.id] === undefined ? true : expanded[m.id];
    out.push(
      <MenuRow
        key={m.id}
        m={m}
        depth={depth}
        kids={kids}
        isOpen={isOpen}
        onToggleExpand={() => ctx.toggleExpand(m.id)}
        onEnabledChange={() => ctx.toggleEnabled(m)}
        onEdit={() => ctx.openEdit(m)}
        onDelete={() => ctx.remove(m)}
        onAddChild={() => ctx.openNew(m.id)}
        onDragStart={() => ctx.setDragId(m.id)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => ctx.onDrop(m.id)}
      />
    );
    if (kids.length > 0 && isOpen) {
      const child = renderTree(kids, byParent, expanded, ctx, depth + 1);
      for (const c of child) out.push(c);
    }
  }
  return out;
}

export default function NavigationBuilder() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/nav-menus");
      setMenus(r.data);
    } catch (e) {
      toast.error("Failed to load menus");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const tree = useMemo(() => {
    const byId = {}; const byPar = {}; const top = [];
    for (const m of menus) {
      byId[m.id] = m;
      if (m.parent_id) {
        if (!byPar[m.parent_id]) byPar[m.parent_id] = [];
        byPar[m.parent_id].push(m);
      } else {
        top.push(m);
      }
    }
    top.sort((a, b) => a.order - b.order);
    Object.values(byPar).forEach((arr) => arr.sort((a, b) => a.order - b.order));
    return { topLevel: top, byParent: byPar, allById: byId };
  }, [menus]);

  const toggleExpand = (id) => setExpanded((e) => Object.assign({}, e, { [id]: !(e[id] === undefined ? true : e[id]) }));
  const openNew = (parent_id = null) => { setEditing(Object.assign({}, EMPTY_MENU, { parent_id })); setDialogOpen(true); };
  const openEdit = (m) => { setEditing(m); setDialogOpen(true); };

  const save = async (payload) => {
    if (editing && editing.id) {
      await api.put("/platform/nav-menus/" + editing.id, payload);
      toast.success("Menu updated");
    } else {
      await api.post("/platform/nav-menus", payload);
      toast.success("Menu added");
    }
    load();
  };

  const remove = async (m) => {
    if (m.is_system) { toast.error("System menu — disable it instead"); return; }
    if (!window.confirm("Delete \"" + m.label + "\" and its submenus?")) return;
    try {
      await api.delete("/platform/nav-menus/" + m.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Delete failed");
    }
  };

  const toggleEnabled = async (m) => {
    await api.put("/platform/nav-menus/" + m.id, { enabled: !m.enabled });
    load();
  };

  const resetAll = async () => {
    if (!window.confirm("Reset the sidebar to system defaults? This wipes all custom menus.")) return;
    await api.post("/platform/nav-menus/reset");
    toast.success("Sidebar reset to defaults");
    load();
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const src = tree.allById[dragId];
    const tgt = tree.allById[targetId];
    if (!src || !tgt) return;
    const parent_id = tgt.parent_id || null;
    const sibs = parent_id ? (tree.byParent[parent_id] || []) : tree.topLevel;
    const filtered = sibs.filter((m) => m.id !== src.id);
    const newList = [];
    for (const m of filtered) {
      if (m.id === targetId) newList.push(Object.assign({}, src, { parent_id }));
      newList.push(m);
    }
    const items = newList.map((m, i) => ({ id: m.id, parent_id, order: i * 10 }));
    try {
      await api.post("/platform/nav-menus/reorder", { items });
      load();
    } catch (e) {
      toast.error("Reorder failed");
    }
    setDragId(null);
  };

  const ctx = { toggleExpand, toggleEnabled, openEdit, openNew, remove, setDragId, onDrop };
  const rows = loading ? [] : renderTree(tree.topLevel, tree.byParent, expanded, ctx);

  return (
    <Card className="p-6" data-testid="nav-builder">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">Sidebar</div>
          <h3 className="font-heading font-bold text-xl">Navigation Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">Drag rows to reorder. Toggle to show/hide. Use “+” to nest a submenu. System entries can be renamed but not deleted.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetAll} data-testid="nav-reset">
            <ArrowsClockwise size={14} className="mr-1.5" />Reset
          </Button>
          <Button size="sm" onClick={() => openNew(null)} data-testid="nav-add">
            <Plus size={14} className="mr-1.5" />New menu
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="space-y-1">
          {rows}
          {!rows.length && <div className="py-12 text-center text-muted-foreground text-sm">No menus yet — click “New menu”.</div>}
        </div>
      )}
      <MenuDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        parents={menus.filter((m) => !m.parent_id)}
        onSave={save}
      />
    </Card>
  );
}
